import ExcelJS from 'exceljs';
import { state } from '../state';
import type { DangerZone, HeatPoint } from './heatmap-types';
import { RULE_KEY_ORDER, RULE_VISUALS } from './heatmap-types';

// Professional Violations Heat Map report (exceljs). Same brand family as Safety, Idling and
// Assets — navy FF1A2332 base, Calibri, canvas charts, heat-coloured cells.

// ── Brand palette ─────────────────────────────────────────────────────────────

const BRAND = {
  navy:      'FF1A2332',
  navyLight: 'FF243347',
  red:       'FFCC1A2D',   // violations / danger accent
  white:     'FFFFFFFF',
  light:     'FFF0F4F8',
  muted:     'FF8AB0CC',
  border:    'FFD0D8E4',
  text:      'FF1A2332',
  hiViolBg:  'FFFFEBEE', hiViol:  'FFC62828',
  midViolBg: 'FFFFF4E0', midViol: 'FF8A5A00',
};

// Rule-specific column header backgrounds (darkened for white text on white Excel bg)
const RULE_HDR: Record<string, string> = {
  hardAccel:      'FFCC0033',
  harshBraking:   'FFBF4500',
  harshCornering: 'FF7A6000',
  speeding:       'FF6600CC',
  seatBelt:       'FF1155CC',
  idling:         'FF009966',
};

// ── Style helpers ─────────────────────────────────────────────────────────────

function fill(argb: string): ExcelJS.FillPattern {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb } };
}
function thinBorder(argb = BRAND.border): Partial<ExcelJS.Borders> {
  const s: ExcelJS.Border = { style: 'thin', color: { argb } };
  return { top: s, left: s, bottom: s, right: s };
}
function rowBorder(): Partial<ExcelJS.Borders> {
  return { bottom: { style: 'thin', color: { argb: 'FFE8EDF2' } } };
}
function applyHeader(row: ExcelJS.Row, bg = BRAND.navyLight, sz = 8): void {
  row.eachCell(cell => {
    cell.fill      = fill(bg);
    cell.font      = { bold: true, color: { argb: BRAND.white }, size: sz, name: 'Calibri' };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border    = thinBorder('FF2E4060');
  });
  row.height = 22;
}

// Violation count heat: high = red, mid = amber, lo = light slate.
function violHeat(count: number, maxCount: number): { fg: string; bg: string } {
  const r = count / (maxCount || 1);
  if (r >= 0.66) return { fg: BRAND.hiViol,  bg: BRAND.hiViolBg.replace('FF', '')  };
  if (r >= 0.25) return { fg: BRAND.midViol, bg: BRAND.midViolBg.replace('FF', '') };
  return               { fg: BRAND.text,    bg: BRAND.light.replace('FF', '')      };
}

// ── Canvas horizontal bar chart → base64 PNG ─────────────────────────────────

function buildBarChartPng(opts: {
  rows: Array<{ label: string; value: number }>;
  barColor: string;
  fmt: (n: number) => string;
}): string {
  const { rows, barColor, fmt } = opts;
  const W = 560, rowH = 26, padTop = 14, padBot = 14;
  const H = Math.max(120, padTop + padBot + rows.length * rowH);

  const canvas = document.createElement('canvas');
  canvas.width  = W * 2;
  canvas.height = H * 2;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(2, 2);
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, W, H);

  const labelW = 150, valW = 70;
  const barX    = labelW + 8;
  const barMaxW = W - labelW - valW - 24;
  const maxVal  = Math.max(...rows.map(r => r.value), 1);

  rows.forEach((r, i) => {
    const y    = padTop + i * rowH;
    const barW = Math.max(r.value > 0 ? 4 : 0, Math.round((r.value / maxVal) * barMaxW));

    ctx.fillStyle = '#334155';
    ctx.font = '600 11px Calibri, sans-serif';
    ctx.textAlign = 'right';
    const label = r.label.length > 24 ? r.label.slice(0, 23) + '…' : r.label;
    ctx.fillText(label, labelW, y + 17);

    ctx.fillStyle = '#EEF2F7';
    ctx.beginPath();
    ctx.roundRect(barX, y + 5, barMaxW, 15, 4);
    ctx.fill();

    if (barW > 0) {
      ctx.fillStyle = barColor;
      ctx.beginPath();
      ctx.roundRect(barX, y + 5, barW, 15, 4);
      ctx.fill();
    }

    ctx.fillStyle = '#1A2332';
    ctx.font = '700 10px Calibri, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(fmt(r.value), barX + barMaxW + 8, y + 17);
  });

  return canvas.toDataURL('image/png');
}

function embedChart(wb: ExcelJS.Workbook, ws: ExcelJS.Worksheet, pngUrl: string, row: number, h: number): void {
  try {
    const base64 = pngUrl.split(',')[1];
    const imgId  = wb.addImage({ base64, extension: 'png' });
    ws.addImage(imgId, { tl: { col: 0, row }, ext: { width: 560, height: h } });
  } catch { /* canvas unavailable */ }
}

// ── Public entry ──────────────────────────────────────────────────────────────

export async function exportHeatmapXlsx(
  zones: DangerZone[],
  allPoints: HeatPoint[],
  fromDate: string,
  toDate: string,
): Promise<void> {
  const wb = new ExcelJS.Workbook();
  wb.creator  = 'FleetLume By EnVue — EnVue Telematics';
  wb.created  = wb.modified = new Date();

  buildSummary      (wb, zones, allPoints, fromDate, toDate);
  buildDangerZones  (wb, zones, fromDate, toDate);
  if (allPoints.length > 0 && allPoints.length <= 50000) {
    buildRawEvents(wb, allPoints, fromDate, toDate);
  }
  buildAbout(wb);

  const buf  = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `heatmap-violations-${state.currentDatabase}-${fromDate}_${toDate}.xlsx`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Sheet 1: Summary ──────────────────────────────────────────────────────────

function buildSummary(
  wb: ExcelJS.Workbook,
  zones: DangerZone[],
  allPoints: HeatPoint[],
  from: string,
  to: string,
): void {
  const ws = wb.addWorksheet('Summary', {
    views: [{ showGridLines: false }],
    properties: { tabColor: { argb: BRAND.red } },
  });
  ws.columns = [{ width: 26 }, { width: 22 }, { width: 22 }, { width: 22 }, { width: 22 }];

  // Title banner
  ws.mergeCells('A1:E1');
  const title = ws.getCell('A1');
  title.value     = 'VIOLATIONS HEAT MAP REPORT';
  title.fill      = fill(BRAND.navy);
  title.font      = { bold: true, size: 16, color: { argb: BRAND.white }, name: 'Calibri' };
  title.alignment = { vertical: 'middle', horizontal: 'center' };
  ws.getRow(1).height = 36;

  ws.mergeCells('A2:E2');
  const sub = ws.getCell('A2');
  sub.value     = 'Powered by FleetLume By EnVue — EnVue Telematics';
  sub.fill      = fill(BRAND.navyLight);
  sub.font      = { size: 9, italic: true, color: { argb: BRAND.muted }, name: 'Calibri' };
  sub.alignment = { vertical: 'middle', horizontal: 'center' };
  ws.getRow(2).height = 16;
  ws.getRow(3).height = 8;

  // Metadata
  const meta: Array<[string, string]> = [
    ['Database',   state.currentDatabase],
    ['Date Range', `${from}  →  ${to}`],
    ['Generated',  new Date().toLocaleString()],
  ];
  meta.forEach(([label, value], i) => {
    const r = ws.getRow(4 + i);
    const a = r.getCell(1); const b = r.getCell(2);
    a.value = label; b.value = value;
    a.fill      = fill(BRAND.light);
    a.font      = { bold: true, size: 9, color: { argb: BRAND.text }, name: 'Calibri' };
    b.font      = { size: 9, color: { argb: BRAND.text }, name: 'Calibri' };
    a.border = b.border = thinBorder();
    r.height = 16;
    ws.mergeCells(`B${4 + i}:E${4 + i}`);
  });

  // KPI section header
  const kpiRow = 4 + meta.length + 1;
  ws.getRow(kpiRow - 1).height = 10;
  ws.mergeCells(`A${kpiRow}:E${kpiRow}`);
  const kh = ws.getCell(`A${kpiRow}`);
  kh.value     = 'KEY PERFORMANCE INDICATORS';
  kh.fill      = fill(BRAND.red);
  kh.font      = { bold: true, size: 9, color: { argb: BRAND.white }, name: 'Calibri' };
  kh.alignment = { vertical: 'middle', horizontal: 'center' };
  ws.getRow(kpiRow).height = 18;

  const totalViol    = zones.reduce((s, z) => s + z.totalViolations, 0);
  const uniqueDevs   = new Set(allPoints.map(p => p.deviceId)).size;
  const uniqueDrvs   = new Set(allPoints.map(p => p.driverId).filter(Boolean)).size;

  const kpis: Array<[string, number, string]> = [
    ['Total Violations', totalViol,       '#,##0'],
    ['Danger Zones',     zones.length,    '#,##0'],
    ['Vehicles Affected', uniqueDevs,     '#,##0'],
    ['Drivers Affected',  uniqueDrvs,     '#,##0'],
  ];
  const labelRow = ws.getRow(kpiRow + 1);
  const valRow   = ws.getRow(kpiRow + 2);
  kpis.forEach(([label, value, fmt], ci) => {
    const lc = labelRow.getCell(ci + 1);
    lc.value     = label;
    lc.fill      = fill(BRAND.light);
    lc.font      = { bold: true, size: 8, color: { argb: BRAND.navy }, name: 'Calibri' };
    lc.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    lc.border    = thinBorder();
    const vc = valRow.getCell(ci + 1);
    vc.value     = value;
    vc.numFmt    = fmt;
    vc.fill      = fill(ci === 0 ? BRAND.hiViolBg.replace('FF', '') : BRAND.white);
    vc.font      = { bold: true, size: 16, color: { argb: ci === 0 ? BRAND.hiViol : BRAND.navy }, name: 'Calibri' };
    vc.alignment = { vertical: 'middle', horizontal: 'center' };
    vc.border    = thinBorder();
  });
  labelRow.height = 26;
  valRow.height   = 32;

  // Rule breakdown KPI row
  const ruleRow = kpiRow + 4;
  ws.getRow(kpiRow + 3).height = 10;
  ws.mergeCells(`A${ruleRow}:E${ruleRow}`);
  const rh = ws.getCell(`A${ruleRow}`);
  rh.value     = 'VIOLATIONS BY RULE TYPE';
  rh.fill      = fill(BRAND.navy);
  rh.font      = { bold: true, size: 9, color: { argb: BRAND.white }, name: 'Calibri' };
  rh.alignment = { vertical: 'middle', horizontal: 'center' };
  ws.getRow(ruleRow).height = 18;

  const enabledRules = RULE_KEY_ORDER.filter(k => zones.some(z => z.byRule[k] != null));
  const ruleCount: Record<string, number> = {};
  for (const z of zones) {
    for (const [k, v] of Object.entries(z.byRule)) {
      if (v) ruleCount[k] = (ruleCount[k] ?? 0) + v;
    }
  }
  const ruleLabelRow = ws.getRow(ruleRow + 1);
  const ruleValRow   = ws.getRow(ruleRow + 2);
  enabledRules.forEach((k, ci) => {
    const lc = ruleLabelRow.getCell(ci + 1);
    lc.value     = RULE_VISUALS[k].label;
    lc.fill      = fill(RULE_HDR[k] ?? BRAND.navyLight);
    lc.font      = { bold: true, size: 8, color: { argb: BRAND.white }, name: 'Calibri' };
    lc.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    lc.border    = thinBorder();
    const vc = ruleValRow.getCell(ci + 1);
    vc.value     = ruleCount[k] ?? 0;
    vc.numFmt    = '#,##0';
    vc.fill      = fill(BRAND.white);
    vc.font      = { bold: true, size: 16, color: { argb: BRAND.navy }, name: 'Calibri' };
    vc.alignment = { vertical: 'middle', horizontal: 'center' };
    vc.border    = thinBorder();
  });
  ruleLabelRow.height = 26;
  ruleValRow.height   = 32;

  // Top 10 danger zones chart
  const chartHdrRow = ruleRow + 4;
  ws.getRow(chartHdrRow - 1).height = 10;
  ws.mergeCells(`A${chartHdrRow}:E${chartHdrRow}`);
  const ch = ws.getCell(`A${chartHdrRow}`);
  ch.value     = 'TOP 10 DANGER ZONES BY VIOLATION COUNT';
  ch.fill      = fill(BRAND.navy);
  ch.font      = { bold: true, size: 9, color: { argb: BRAND.white }, name: 'Calibri' };
  ch.alignment = { vertical: 'middle', horizontal: 'center' };
  ws.getRow(chartHdrRow).height = 18;

  const top10 = [...zones].sort((a, b) => b.totalViolations - a.totalViolations).slice(0, 10);
  const png   = buildBarChartPng({
    rows:     top10.map(z => ({
      label: z.locationName ?? `${z.centroidLat.toFixed(3)}, ${z.centroidLng.toFixed(3)}`,
      value: z.totalViolations,
    })),
    barColor: '#CC1A2D',
    fmt:      n => n.toLocaleString(),
  });
  embedChart(wb, ws, png, chartHdrRow, Math.max(120, top10.length * 26 + 28));
}

// ── Sheet 2: Danger Zones ─────────────────────────────────────────────────────

function buildDangerZones(
  wb: ExcelJS.Workbook,
  zones: DangerZone[],
  from: string,
  to: string,
): void {
  const enabledRules = RULE_KEY_ORDER.filter(k => zones.some(z => z.byRule[k] != null));

  const ws = wb.addWorksheet('Danger Zones', {
    views: [{ state: 'frozen', ySplit: 2, showGridLines: false }],
    properties: { tabColor: { argb: BRAND.red } },
  });

  const colDefs = [
    { key: 'rank',     width: 6  },
    { key: 'location', width: 36 },
    { key: 'lat',      width: 12 },
    { key: 'lng',      width: 12 },
    { key: 'total',    width: 12 },
    { key: 'dominant', width: 18 },
    { key: 'vehicles', width: 12 },
    { key: 'drivers',  width: 12 },
    { key: 'peak',     width: 12 },
    { key: 'radius',   width: 12 },
    ...enabledRules.map(k => ({ key: k, width: 14 })),
  ];
  ws.columns = colDefs;

  // Title row
  ws.mergeCells(1, 1, 1, colDefs.length);
  const titleCell = ws.getCell('A1');
  titleCell.value     = `Danger Zones — ${from} to ${to}`;
  titleCell.fill      = fill(BRAND.navy);
  titleCell.font      = { bold: true, size: 11, color: { argb: BRAND.white }, name: 'Calibri' };
  titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
  ws.getRow(1).height = 24;

  // Header row — base columns in navyLight, rule columns in their own colors
  const hdrRow = ws.getRow(2);
  const hdrLabels = [
    '#', 'Location', 'Lat', 'Lng', 'Total', 'Dominant Rule',
    'Vehicles', 'Drivers', 'Peak Hour', 'Radius (m)',
    ...enabledRules.map(k => RULE_VISUALS[k].label),
  ];
  hdrLabels.forEach((label, i) => {
    const cell = hdrRow.getCell(i + 1);
    cell.value     = label;
    const ruleKey  = enabledRules[i - 10];
    cell.fill      = fill(ruleKey ? (RULE_HDR[ruleKey] ?? BRAND.navyLight) : BRAND.navyLight);
    cell.font      = { bold: true, color: { argb: BRAND.white }, size: 8, name: 'Calibri' };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border    = thinBorder('FF2E4060');
  });
  hdrRow.height = 22;

  ws.autoFilter = { from: { row: 2, column: 1 }, to: { row: 2, column: colDefs.length } };

  const maxViol = Math.max(...zones.map(z => z.totalViolations), 1);

  zones.forEach((z, idx) => {
    const row   = ws.getRow(3 + idx);
    const rowBg = idx % 2 === 0 ? BRAND.white : BRAND.light.replace('FF', '');
    const heat  = violHeat(z.totalViolations, maxViol);

    const domRuleColor = RULE_HDR[z.dominantRule] ?? BRAND.navyLight;

    const vals: Array<string | number> = [
      z.rank,
      z.locationName ?? `${z.centroidLat.toFixed(4)}, ${z.centroidLng.toFixed(4)}`,
      +z.centroidLat.toFixed(6),
      +z.centroidLng.toFixed(6),
      z.totalViolations,
      RULE_VISUALS[z.dominantRule].label,
      z.uniqueDeviceIds.size,
      z.uniqueDriverIds.size,
      z.peakHour != null ? `${String(z.peakHour).padStart(2, '0')}:00` : '—',
      Math.round(z.radiusM),
      ...enabledRules.map(k => z.byRule[k] ?? 0),
    ];

    vals.forEach((val, ci) => {
      const cell = row.getCell(ci + 1);
      cell.value     = val;
      cell.border    = rowBorder();
      cell.alignment = { vertical: 'middle', horizontal: ci === 1 ? 'left' : 'center' };
      cell.font      = { size: 9, name: 'Calibri', color: { argb: BRAND.text } };

      if (ci === 0) {
        // Rank
        cell.fill = fill(BRAND.light.replace('FF', ''));
        cell.font = { size: 8, name: 'Calibri', bold: true, color: { argb: BRAND.muted } };
      } else if (ci === 4) {
        // Total violations — heat coloured
        cell.fill = fill(heat.bg);
        cell.font = { bold: true, size: 9, name: 'Calibri', color: { argb: heat.fg } };
        cell.numFmt = '#,##0';
      } else if (ci === 5) {
        // Dominant rule — colored text matching the rule
        cell.fill = fill(rowBg);
        cell.font = { bold: true, size: 8, name: 'Calibri', color: { argb: domRuleColor } };
      } else if (ci >= 10) {
        // Rule-specific violation counts
        const n       = val as number;
        const ruleHeat = violHeat(n, maxViol);
        cell.fill   = n > 0 ? fill(ruleHeat.bg) : fill(rowBg);
        cell.font   = { size: 9, name: 'Calibri', color: { argb: n > 0 ? ruleHeat.fg : BRAND.muted } };
        cell.numFmt = '#,##0';
      } else {
        cell.fill = fill(rowBg);
        if (ci === 2 || ci === 3) cell.numFmt = '0.000000';
        if (ci === 9) cell.numFmt = '#,##0';
      }
    });
    row.height = 16;
  });
}

// ── Sheet 3: Raw Events ───────────────────────────────────────────────────────

function buildRawEvents(
  wb: ExcelJS.Workbook,
  allPoints: HeatPoint[],
  from: string,
  to: string,
): void {
  const ws = wb.addWorksheet('Raw Events', {
    views: [{ state: 'frozen', ySplit: 2, showGridLines: false }],
    properties: { tabColor: { argb: BRAND.navyLight } },
  });
  ws.columns = [
    { key: 'ts',       width: 22 },
    { key: 'rule',     width: 18 },
    { key: 'lat',      width: 14 },
    { key: 'lng',      width: 14 },
    { key: 'deviceId', width: 24 },
    { key: 'driverId', width: 24 },
  ];

  // Title row
  ws.mergeCells('A1:F1');
  const titleCell = ws.getCell('A1');
  titleCell.value     = `Raw Violation Events — ${from} to ${to}  (${allPoints.length.toLocaleString()} events)`;
  titleCell.fill      = fill(BRAND.navy);
  titleCell.font      = { bold: true, size: 11, color: { argb: BRAND.white }, name: 'Calibri' };
  titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
  ws.getRow(1).height = 24;

  // Header row
  const hdrRow = ws.getRow(2);
  ['Timestamp', 'Rule', 'Latitude', 'Longitude', 'Vehicle ID', 'Driver ID']
    .forEach((h, i) => { hdrRow.getCell(i + 1).value = h; });
  applyHeader(hdrRow);
  ws.autoFilter = { from: { row: 2, column: 1 }, to: { row: 2, column: 6 } };

  allPoints.forEach((p, idx) => {
    const row   = ws.getRow(3 + idx);
    const rowBg = idx % 2 === 0 ? BRAND.white : BRAND.light.replace('FF', '');
    const ruleColor = RULE_HDR[p.ruleKey] ?? BRAND.navyLight;

    const vals: Array<string | number> = [
      p.timestamp,
      RULE_VISUALS[p.ruleKey].label,
      +p.latitude.toFixed(6),
      +p.longitude.toFixed(6),
      p.deviceId,
      p.driverId ?? '',
    ];
    vals.forEach((val, ci) => {
      const cell = row.getCell(ci + 1);
      cell.value     = val;
      cell.fill      = fill(rowBg);
      cell.border    = rowBorder();
      cell.alignment = { vertical: 'middle', horizontal: ci >= 2 ? 'center' : 'left' };
      cell.font      = { size: 9, name: 'Calibri', color: { argb: ci === 1 ? ruleColor : BRAND.text } };
      if (ci === 1) cell.font = { bold: true, size: 9, name: 'Calibri', color: { argb: ruleColor } };
      if (ci === 2 || ci === 3) cell.numFmt = '0.000000';
    });
    row.height = 14;
  });
}

// ── Sheet 4: About ────────────────────────────────────────────────────────────

function buildAbout(wb: ExcelJS.Workbook): void {
  const ws = wb.addWorksheet('About', {
    views: [{ showGridLines: false }],
    properties: { tabColor: { argb: BRAND.muted } },
  });
  ws.columns = [{ width: 26 }, { width: 74 }];

  ws.mergeCells('A1:B1');
  const t = ws.getCell('A1');
  t.value     = 'HOW THE HEAT MAP & DANGER ZONES WORK';
  t.fill      = fill(BRAND.navy);
  t.font      = { bold: true, size: 13, color: { argb: BRAND.white }, name: 'Calibri' };
  t.alignment = { vertical: 'middle', horizontal: 'center' };
  ws.getRow(1).height = 28;

  const rows: Array<[string, string, boolean]> = [
    ['DATA SOURCE', '', true],
    ['Violation events', 'Geotab ExceptionEvent records for the selected vehicles, rules, and date range. Each event is geo-located via GPS correlation (nearest DeviceStatusInfo fix within 5 minutes).', false],
    ['', '', false],
    ['DANGER ZONE DETECTION', '', true],
    ['Clustering algorithm', 'DBSCAN (Density-Based Spatial Clustering of Applications with Noise). Groups GPS points that are within a configurable radius of each other. Isolated outlier points that do not reach the minimum cluster density are excluded.', false],
    ['Spatial index', 'A grid-based spatial index is used for DBSCAN neighbour lookups so clustering scales to 100k+ points without O(n²) distance comparisons.', false],
    ['Zone rank', 'Zones are ranked by total violation count descending. Rank #1 = the most dangerous location in the analysis period.', false],
    ['Dominant rule', 'The violation type with the highest count within that zone.', false],
    ['Peak hour', 'The hour-of-day (0–23, local time) with the most violations for that zone.', false],
    ['Radius', 'Approximate radius (metres) enclosing all cluster points from the centroid.', false],
    ['', '', false],
    ['HEAT COLOUR GUIDE', '', true],
    ['Red  (≥ 66% of max)', 'High violation density. Immediate attention recommended for this location.', false],
    ['Amber  (25–65%)',     'Moderate violation density. Monitor and consider targeted coaching or signage.', false],
    ['Light  (< 25%)',      'Low violation density relative to worst zone in this period.', false],
    ['', '', false],
    ['BOUNDED ANALYSIS', '', true],
    ['Data cap',    'The heat map applies per-rule and per-device event caps to keep analysis fast and memory-bounded. It is a density visualisation, not an exact event count — use the Idling Cost or Safety Scorecard reports for precise event totals.', false],
  ];

  rows.forEach(([label, text, isSection], i) => {
    const r = ws.getRow(2 + i);
    const a = r.getCell(1); const b = r.getCell(2);
    if (isSection) {
      ws.mergeCells(2 + i, 1, 2 + i, 2);
      a.value     = label;
      a.fill      = fill(BRAND.red);
      a.font      = { bold: true, size: 10, color: { argb: BRAND.white }, name: 'Calibri' };
      a.alignment = { vertical: 'middle' };
      a.border    = thinBorder('FF2E4060');
      r.height    = 20;
    } else if (label || text) {
      a.value = label; b.value = text;
      a.fill  = fill(BRAND.light.replace('FF', ''));
      b.fill  = fill(BRAND.white);
      a.font  = { bold: true, size: 9, name: 'Calibri', color: { argb: BRAND.text } };
      b.font  = { size: 9, name: 'Calibri', color: { argb: BRAND.text } };
      a.alignment = { vertical: 'middle', wrapText: true };
      b.alignment = { vertical: 'middle', wrapText: true };
      a.border = b.border = thinBorder();
      r.height = text.length > 90 ? 40 : 18;
    }
  });
}
